import { MarketingEmailCollector } from '../../../domain/marketing-email-collector';
import {
  CreateMarketingPageItemEmailRequestApiModel,
  GenericLayoutApiModel,
  MarketingPageItemEmailApiModel,
  TextStyleApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  createLocalizedText,
  mapDomainImageDataToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
  titleLocalizedFromHeadlineMapper,
} from '../../mapping/create-tray-domain-to-api';
import { MarketingItemLayout } from '../../../domain/domain-layouts';
import {
  commonMapper,
  headlineTextMapper,
  mapApiModelImageWithPlatformToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { MarketingEmailCollectorTrayViewModel } from './marketing-email-collector-tray-form.view-model';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
  mapMarketingItemLayoutToTraySelectorType,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  mapTraySelectorTypeToMarketingItemLayout,
  mapViewModelImagesToLocalizedDomainImages,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'MarketingEmailCollector'

/**
 * DOMAIN -> API
 */

const mapDomainEmailCollectorLayoutToApi = (template: MarketingItemLayout): GenericLayoutApiModel => {
  switch (template) {
    case MarketingItemLayout.EMAIL_CENTERED:
      return GenericLayoutApiModel.Centered;
    case MarketingItemLayout.EMAIL_LEFT:
      return GenericLayoutApiModel.Left;
    case MarketingItemLayout.EMAIL_RIGHT:
      return GenericLayoutApiModel.Right;
  }
};

export const mapDomainEmailCollectorToApi = (
  emailCollector: MarketingEmailCollector,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingPageItemEmailRequestApiModel => {
  const commonProperties = mapperPipe(
    emailCollector,
    titleLocalizedFromHeadlineMapper,
    scheduledDateMapperDomainToApi,
    targetingMapperDomainToApi
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    hintTextLocalized: createLocalizedText('', ''),
    inputLabelLocalized: createLocalizedText(emailCollector.inputFieldLabelEnglish, emailCollector.inputFieldLabelArabic),
    sublineTextLocalized: createLocalizedText('', ''),
    buttonTextLocalized: createLocalizedText(emailCollector.buttonTextEnglish, emailCollector.buttonTextArabic),
    name: emailCollector.titleEnglish,
    layout: mapDomainEmailCollectorLayoutToApi(emailCollector.template),
    backgroundImages: emailCollector.backgroundImages.map((image) => ({
      en: image.en.map((localizedImage) => mapDomainImageDataToApi(localizedImage)),
      ar: image.ar.map((localizedImage) => mapDomainImageDataToApi(localizedImage)),
      order: image.order,
    })),
    textStyle: TextStyleApiModel.Light,
    tenantId,
    platformIds: emailCollector.platformIds,
  }));
};

/**
 * API -> DOMAIN
 */

const mapApiEmailCollectorLayoutToDomain = (template: GenericLayoutApiModel): MarketingItemLayout => {
  switch (template) {
    case GenericLayoutApiModel.Centered:
      return MarketingItemLayout.EMAIL_CENTERED;
    case GenericLayoutApiModel.Left:
      return MarketingItemLayout.EMAIL_LEFT;
    case GenericLayoutApiModel.Right:
      return MarketingItemLayout.EMAIL_RIGHT;
  }
};

export const mapApiEmailCollectorDomain = (emailCollector: MarketingPageItemEmailApiModel): MarketingEmailCollector => {
  return remainderMapper(
    mapperPipe(emailCollector, commonMapper, headlineTextMapper, scheduledDateMapperApiToDomain, targetingMapperApiToDomain),
    () => ({
      type: 'MarketingEmailCollector',
      titleEnglish: emailCollector.name,
      buttonTextEnglish: emailCollector.buttonTextLocalized.en,
      buttonTextArabic: emailCollector.buttonTextLocalized.ar,
      inputFieldLabelEnglish: emailCollector.inputLabelLocalized.en,
      inputFieldLabelArabic: emailCollector.inputLabelLocalized.ar,
      template: mapApiEmailCollectorLayoutToDomain(emailCollector.layout),
      backgroundImages: emailCollector.backgroundImages.map((image, order) => ({
        order: image.order || order,
        en: image.en.map(mapApiModelImageWithPlatformToDomain),
        ar: image.ar.map(mapApiModelImageWithPlatformToDomain),
      })),
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainEmailCollectorToViewModel = (domainModel: MarketingEmailCollector): MarketingEmailCollectorTrayViewModel => {
  return {
    id: domainModel.id || '',
    moduleName: domainModel.titleEnglish,
    headlineText: { en: domainModel.headlineTextEnglish, ar: domainModel.headlineTextArabic },
    inputFieldLabel: { en: domainModel.inputFieldLabelEnglish, ar: domainModel.inputFieldLabelArabic },
    buttonText: { en: domainModel.buttonTextEnglish, ar: domainModel.buttonTextArabic },
    scheduling: {
      schedulingType: domainModel.scheduledFrom || domainModel.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: domainModel.scheduledFrom ? new Date(domainModel.scheduledFrom).toISOString() : undefined,
      scheduledTo: domainModel.scheduledTo ? new Date(domainModel.scheduledTo).toISOString() : undefined,
    },
    template: mapMarketingItemLayoutToTraySelectorType(domainModel.template),
    backgroundImages: domainModel.backgroundImages.map((image) => mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(image)),
    userSegmentIds: domainModel.userSegmentIds,
    type: TrayFormViewModelType.EMAIL,
    countryIds: domainModel.countryIds,
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapViewModelEmailCollectorToDomain = (
  emailCollector: MarketingEmailCollectorTrayViewModel,
  platformIds: Record<PlatformType, string>
): MarketingEmailCollector => {
  return {
    id: emailCollector.id || '',
    titleEnglish: emailCollector.moduleName,
    headlineTextEnglish: emailCollector.headlineText.en,
    headlineTextArabic: emailCollector.headlineText.ar,
    inputFieldLabelEnglish: emailCollector.inputFieldLabel.en,
    inputFieldLabelArabic: emailCollector.inputFieldLabel.ar,
    buttonTextEnglish: emailCollector.buttonText.en,
    buttonTextArabic: emailCollector.buttonText.ar,
    backgroundImages: emailCollector.backgroundImages.map((image, index) =>
      mapViewModelImagesToLocalizedDomainImages(image, platformIds, index)
    ),
    scheduledFrom: emailCollector.scheduling?.scheduledFrom ? new Date(emailCollector.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: emailCollector.scheduling?.scheduledTo ? new Date(emailCollector.scheduling.scheduledTo).getTime() : undefined,
    template: mapTraySelectorTypeToMarketingItemLayout(emailCollector.template),
    userSegmentIds: emailCollector.userSegmentIds,
    countryIds: emailCollector.countryIds,
    type: 'MarketingEmailCollector',
    order: undefined,
    visible: undefined,
    draggable: true,
    platformIds: Object.values(platformIds),
  };
};
